import * as THREE from 'three';
// eslint-disable-next-line no-unused-vars
import Node from '@/models/Node';
import Track from './Track';
// eslint-disable-next-line no-unused-vars
import Vue from 'vue';
// eslint-disable-next-line no-unused-vars
import Camera from '@/models/DOMCamera';

export default class TimelineLayout
{
    public static readonly maxAllocatedTrackHeight : number = 150;
    public static rootTrack : Track = new Track();
    public static processed : Set<Node> = new Set<Node>();
    public static trackList : Track[] = []
    private static _lastGeneratedArray : Node[] = []
    private static _lastViewer : any = null
    private static _lastCamera : any = null;

    public static get ActiveCamera()
    {
        return this._lastCamera;
    }

    public static recomputeLayout()
    {
        if (this._lastGeneratedArray.length > 0)
        {
            this.generateLayout(this._lastGeneratedArray, this._lastViewer);
        }
    }

    public static generateLayout(nodes : Node[], camera : Camera)
    {
        this._lastCamera = camera;
        const viewer = null;
        this._lastViewer = viewer;
        this._lastGeneratedArray.length = 0;
        for(let i = 0; i < nodes.length; ++i)
        {
            this._lastGeneratedArray.push(nodes[i]);
        }

        // if (process.env.NODE_ENV === 'development')
        // {
        // 	console.log('regenerating layout');
        // }
        
        // Generate complete Track linked list
        // TODO: starting with extremely naive algorithm; needs to implement the proper algorithm I've planned out.
        // this.rootTrack = new Track();
        // for (const n of nodes)
        // {
        //     this.rootTrack.findXFit(n);
        // }
        // PLANNED ALGORITHM:
        // Find parentless nodes, for each:
        //     findXFit in main linked list
        //     create temp root track (a sub-linked-list that will get patched into the main one)
        //     sort children in reverse start date order, for each:
        //         findXFit in temp track list
        //         recurse into its children (doing the same sort and findXFit in sub track list)
        //     patch temp track list into outer track list between current node's track and nextTrack
        // Now you should have a full track list with all nodes included, but lots of unnecessary space/tracks, so...
        // Start from root track, for each track:
        //     for each node in the track:
        //         keep moving node to preceding track until such a move is impossible
        // Start from root track, for each track:
        //     if track is empty, patch it out of the list
        const nodesSet = new Set(nodes);
        this.rootTrack = new Track();
        this.processed.clear();
        const parentlessNodes = [];
        for (const n of nodes)
        {
            if (n.isParentlessFiltered(nodesSet))
            {
                parentlessNodes.push(n);
            }
        }
        
        parentlessNodes.sort((a, b) =>
        {
            const val = (b.dateEnd.valueOf() - b.dateStart.valueOf()) - (a.dateEnd.valueOf() - a.dateStart.valueOf());
            if (val != 0)
                return val;
            return b.name.localeCompare(a.name);
        });
        
        for (const n of parentlessNodes)
        {
            if (n.isChildlessFiltered(nodesSet))
            {
                this.rootTrack.findXFit(n);
            }
            else
            {
                const subTrack = this.buildSubTrack(nodesSet, n, true);
                this.rootTrack.insertEnd(subTrack);
            }
        }
        
        let t = this.rootTrack.nextTrack;
        while (t != null)
        {
            if (t.nodes != null)
            {
                const tempArray = new Array(...t.nodes);
                for (const n of tempArray)
                {
                    // move node down as far as you can
                    let current = t;
                    let down = t.previousTrack;
                    while (down != null)
                    {
                        if (down.moveIfXFit(n, current))
                        {
                            current = down;
                        }
                        else
                        {
                            break; // can't move anymore
                        }
                        down = down.previousTrack;
                    }
                }
            }
            t = t.nextTrack;
        }
        t = this.rootTrack.nextTrack;
        while (t != null)
        {
            if (t.nodes == null || t.nodes.length === 0) // remove empty track
            {
                t.previousTrack.nextTrack = t.nextTrack;
                if (t.nextTrack != null)
                {
                    t.nextTrack.previousTrack = t.previousTrack;
                }
            }
            t = t.nextTrack;
        }
        
        this.initTrackList();

        // after the Track linked list is fully generated, computeY and adjustCamera should always be the last step of the layout algorithm.
        // ^^^ Not anymore, now that we're not using Three.js
        //let layoutHeight = this.computeY();
        //this.adjustCamera(viewer, layoutHeight);
        //this.applyY();
    }

    private static initTrackList()
    {
        this.trackList.length = 0;
        let t = this.rootTrack;
        let id = 0;
        while (t != null)
        {
            for(const node of t.nodes)
            {
                node.trackID = id;
            }
            ++id;
            this.trackList.push(t);
            t = t.nextTrack;
        }
    }

    private static buildSubTrack(nodesSet: Set<Node>, n : Node, includeN : Boolean = false) : Track
    {
        if (n.isChildlessFiltered(nodesSet))
        {
            return <Track><unknown>null;
        }
        
        const subTrack = new Track();
        if (includeN)
        {
            subTrack.findXFit(n);
        }
        n.forAllChildren((child) =>
        {
            if (this.processed.has(child) || !(nodesSet.has(child)))
            {
                return;
            }
            this.processed.add(child);

            // TODO: I think this might not do the intended thing, because it lets grandchildren get b/w their grandparents and uncles/aunts. not sure.
            const childBaseTrack = subTrack.findXFit(child);
            if (childBaseTrack != null) // things may not be initialized yet; avoid errors, end silently
            {
                const subSubTrack = this.buildSubTrack(nodesSet, child);
                childBaseTrack.insertAfter(subSubTrack);
            }
        });
        return subTrack;
    }

    public static setYForAllTracks(totalHeight : number)
    {
        let totalWeight = 0;
        let t = this.rootTrack;
        while (t != null)
        {
            totalWeight += t.computeHeight();
            t = t.nextTrack;
        }
        let h = 0;
        t = this.rootTrack;
        while (t != null)
        {
            const allocatedHeightForTrack = Math.min(this.maxAllocatedTrackHeight, (t.height / totalWeight) * totalHeight);
            t.y = h + (allocatedHeightForTrack * 0.5);
            t.applyY(allocatedHeightForTrack);
            h += allocatedHeightForTrack;
            t = t.nextTrack;
        }
        //console.log(`${h-totalHeight} should be 0`);
    }

    /**
     * Given complete Track linked list (derived from x collisions), use heights to set the y of every track based on the previous track.
     * Returns the total height of the layout, so the camera can adjust.
     * (NOTE: used by Three.js solution and not the DOM solution)
     */
    private static computeY() : number
    {
        let h = 0;
        let t = this.rootTrack;
        while (t != null)
        {
            t.y = (t.computeHeight() * 0.5) + h;
            h += t.height;
            t = t.nextTrack;
        }

        /*
        t = this.rootTrack;
        let i = 0;
        while (t != null)
        {
            if (t.nodes.length > 0)
            {
                console.log(`Track ${i} (${t.y})`);
                for(let node of t.nodes)
                {
                    console.log(`\t${node.name}`);
                }
            }
            t = t.nextTrack;
            ++i;
        }*/
        return h;
    }

    /**
     * Given the layoutHeight, set the camera parameters so that aspect ratio is unaffected but the whole layout can be encapsulated.
     * Camera's bottom should be set so that the first Track is aligned to it (with room for year labels).
     */
    private static adjustCamera(viewer : any, layoutHeight : number)
    {
        if (viewer.$refs.camera && viewer.$refs.camera.inst != null)
        {
            const newBot = -20; // TODO: adjust this as necessary to consistently leave room for year labels
            const newTop = layoutHeight + 40; // TODO: adjust this as necessary to consistently leave room for era labels

            //console.log(`old vertical range: ${(viewer.$refs.camera.inst.top - viewer.$refs.camera.inst.bottom)}; new vertical range: ${(newTop - newBot)}`);
            
            // console.log(viewer.$refs.camera.inst.projectionMatrix);

            viewer.camZoomFactor = THREE.Math.clamp((viewer.$refs.camera.inst.top - viewer.$refs.camera.inst.bottom) / (newTop - newBot), 0.25, 1.38);
            viewer.$refs.camera.vglNamespace.update();
            viewer.$refs.camera.inst.position.y = (viewer.$refs.camera.inst.top + newBot) / viewer.$refs.camera.zoom;

            // ... lol yeah VueGL's renderer does not give control over these params... simply using zoom instead works
            // let factor = (newTop - newBot) / (camera.inst.top - camera.inst.bottom);
            // let newHalfWidth = (camera.inst.right - camera.inst.left) * factor * 0.5;
            // let newHalfHeight = (newTop - newBot) * 0.5;
            // camera.inst.position.y = (newBot + newTop) * 0.5;
            // camera.inst.bottom = newBot; // -newHalfHeight;
            // camera.inst.top = newTop; // newHalfHeight;
            // camera.inst.left = -newHalfWidth + camera.inst.position.x;
            // camera.inst.right = newHalfWidth + camera.inst.position.x;
            // camera.inst.updateProjectionMatrix();
            // camera.inst.projectionMatrix.set(
            //     2/(camera.inst.right-camera.inst.left),0,0,-(camera.inst.right+camera.inst.left)/(camera.inst.right-camera.inst.left),
            //     0,2/(camera.inst.top-camera.inst.bottom),0,-(camera.inst.top+camera.inst.bottom)/(camera.inst.top-camera.inst.bottom),
            //     0,0,-2/(camera.inst.far-camera.inst.near),-(camera.inst.far+camera.inst.near)/(camera.inst.far-camera.inst.near),
            //     0,0,0,1
            // );
            // console.log(`new cam params: ${camera.inst.top}, ${camera.inst.bottom}, ${camera.inst.left}, ${camera.inst.right}, ${camera.inst.position.y}`);
            
            // console.log(viewer.$refs.camera.inst.projectionMatrix);
        }
    }

    public static lerpTowardY(print: Boolean=false)
    {
        let t = this.rootTrack;
        let i = 0;
        while (t != null)
        {
            t.lerpTowardY();
            if (print)
                console.log(`${i}: ${t.y}`);
            t = t.nextTrack;
            ++i;
        }
    }

    public static applyY()
    {
        let t = this.rootTrack;
        while (t != null)
        {
            t.applyY();
            t = t.nextTrack;
        }
    }
}